import Image from 'next/image';
import PropTypes from 'prop-types';
import { useState } from 'react';
import ReactModal from 'react-modal';
import { usePcConfigurator } from 'utils/PcConfiguratorContext';
import classnames from 'classnames';
import styles from './styles';
import PcModelModalHelp from '../PcModelModalHelp';
import PcModelDelayMessage from '../PcModelDelayMessage';

const PcModelDelayModal = ({ currentOptions, variant }) => {
  const delayedOptions = currentOptions?.filter((o) => !o.in_stock);
  const { pcConfigDoc } = usePcConfigurator();
  const [isOpen, setIsOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    setIsHelpModalOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsHelpModalOpen(false);
  };

  const handleHelpModalOpen = () => {
    setIsOpen(false);
    setIsHelpModalOpen(true);
  };

  if (!delayedOptions.length) return null;

  const needHelpTitle = pcConfigDoc?.data?.need_help_title;

  return (
    <>
      {variant === 'icon' ? (
        <div className="icon-variant">
          <button type="button" onClick={handleOpen}>
            <Image draggable={false}
              src="https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-exclamation-blue.svg?v=1714039765"
              alt="Error"
              width={24}
              height={24}
            />
          </button>
        </div>
      ) : (
        <div className={classnames('alert', { 'alert-hover': isHovered })}>
          <div className="icon">
            <Image draggable={false}
              src="https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-exclamation-blue.svg?v=1714039765"
              alt="Error"
              width={16}
              height={16}
            />
          </div>
          <div>
            <span>
              {`(${delayedOptions.length})`} item/s will cause a delay.{' '}
            </span>{' '}
            <a
              className="view-issues"
              href="#"
              onClick={handleOpen}
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
            >
              View
            </a>
          </div>
        </div>
      )}

      <ReactModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className="modal-content"
        overlayClassName="modal-overlay"
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        preventScroll
        style={{
          overlay: {
            background: 'rgba(255, 255, 255, 0.25)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
          },
          content: {
            margin: '10px',
            borderRadius: '2px',
            minWidth: '50vw',
            overflow: 'hidden',
          },
        }}
      >
        <div className="modal-wrapper">
          <div className="modal-header">
            <div className="modal-header-title">We need a little time</div>

            {/* <div className="modal-header-icon" onClick={handleClose} /> */}
            <div 
              onClick={handleClose} 
              className="modal-header-icon"
              style={{ backgroundImage: `url('https://cdn.shopify.com/s/files/1/0522/3320/7988/files/icon-close.png?v=1714038756')` }}
              onMouseEnter={(event) => { 
                event.target.style.backgroundImage = "url('https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-close-hover_1.png?v=1714063075')";
              }}
              onMouseLeave={(event) => { 
                event.target.style.backgroundImage = `url('https://cdn.shopify.com/s/files/1/0522/3320/7988/files/icon-close.png?v=1714038756')`;
              }}
            />
          </div>

          <div className="modal-body">
            <div className="body-message">
              Unfortunately, we have had a delay on incoming stock of one or
              more of your selected components. If you'd like your order to come
              sooner, feel free to change the following component/s to any of
              our in stock items -
            </div>
            <div className="errors">
              {delayedOptions.map((option) => (
                <div className="error" key={option.id}>
                  <div className="icon">
                    <Image draggable={false}
                      src="https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-exclamation-blue.svg?v=1714039765"
                      alt="Error"
                      width={16}
                      height={16}
                    />
                  </div>

                  <PcModelDelayMessage
                    option={option}
                    closeModal={handleClose}
                  />
                </div>
              ))}
            </div>
            <div className="further-message">
              We apologise for any inconvenience caused. Please feel free to
              reach out if you have any questions.
            </div>
          </div>

          <div className="modal-footer">
            <div className="need-help" onClick={handleHelpModalOpen}>
              {needHelpTitle}
            </div>
          </div>
        </div>
      </ReactModal>

      <PcModelModalHelp
        title={needHelpTitle}
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        pcConfigDoc={pcConfigDoc}
      />
      <style jsx>{styles}</style>
    </>
  );
};

PcModelDelayModal.propTypes = {
  currentOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  variant: PropTypes.oneOf(['toast', 'icon']),
};

PcModelDelayModal.defaultProps = {
  variant: 'toast',
};

export default PcModelDelayModal;
